import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../context/AuthContext";
import { RefreshCw, Users, PoundSterling, Heart, MailCheck } from "lucide-react";

const gbp = (pence) => `£${(pence / 100).toFixed(2).replace(/\.00$/, "")}`;

const TYPE_LABEL = {
  membership_year_10: "Membership £10/yr",
  membership_year_20: "Membership £20/yr",
  membership_year_50: "Membership £50/yr",
  donation_open: "Donation",
};

const STATUS_STYLE = {
  paid: "bg-[#1A5DFF] text-[#FFF8E7]",
  pending: "bg-[#FFD93D] text-[#0A0A0A]",
  failed: "bg-[#FF2E63] text-[#FFF8E7]",
  expired: "bg-[#2C2C2C] text-[#FFF8E7]",
  refunded: "bg-[#0A0A0A] text-[#FFF8E7]",
};

export default function AdminPayments() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setBusy(true); setError(null);
    try {
      const r = await axios.get(`${API}/admin/payments`, { withCredentials: true });
      setData(r.data);
    } catch {
      setError("Could not load payments.");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const totals = data?.totals;
  const txns = data?.transactions || [];

  const stats = totals ? [
    { icon: Users, label: "Active members", value: totals.active_members, bg: "bg-[#FF2E63]", text: "text-[#FFF8E7]" },
    { icon: PoundSterling, label: "Membership revenue", value: gbp(totals.membership_revenue), bg: "bg-[#FFD93D]", text: "text-[#0A0A0A]" },
    { icon: Heart, label: "Donations total", value: gbp(totals.donation_total), bg: "bg-[#1A5DFF]", text: "text-[#FFF8E7]" },
    { icon: Heart, label: "Donations count", value: totals.donations_count, bg: "bg-white", text: "text-[#0A0A0A]" },
  ] : [];

  return (
    <div data-testid="admin-tab-payments">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading font-black text-[#0A0A0A] text-2xl tracking-tight">Payments</h2>
          <p className="text-sm text-[#2C2C2C]">{txns.length} transactions · totals count paid payments only</p>
        </div>
        <button onClick={load} disabled={busy} data-testid="admin-payments-refresh" className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] rounded-none px-4 py-2 text-sm font-medium inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>

      {error && <div className="bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A] p-3 mb-4" role="alert">{error}</div>}

      {totals && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-payments-totals">
          {stats.map((s) => (
            <div key={s.label} className={`${s.bg} ${s.text} border-2 border-[#0A0A0A] pop-shadow p-5`}>
              <s.icon className="w-5 h-5 mb-2" />
              <div className="font-heading font-black text-3xl leading-none">{s.value}</div>
              <div className="text-xs uppercase tracking-[0.14em] font-bold mt-2 opacity-90">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="overflow-x-auto border-2 border-[#0A0A0A] bg-white" data-testid="admin-payments-table">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="border-b-2 border-[#0A0A0A] bg-[#FFE8A8] text-xs uppercase tracking-[0.14em]">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Welcome email</th>
            </tr>
          </thead>
          <tbody>
            {txns.map((t) => (
              <tr key={t.session_id} className="border-b border-[#0A0A0A]/20" data-testid={`payment-row-${t.session_id}`}>
                <td className="px-4 py-3 whitespace-nowrap text-[#2C2C2C]">{t.created_at ? new Date(t.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—"}</td>
                <td className="px-4 py-3 whitespace-nowrap font-bold">{TYPE_LABEL[t.lookup_key] || t.lookup_key}</td>
                <td className="px-4 py-3">{t.user_email || <span className="text-[#2C2C2C]">anonymous donor</span>}</td>
                <td className="px-4 py-3 font-bold whitespace-nowrap">{t.payment_status === "paid" ? gbp(t.amount || 0) : "—"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block text-xs uppercase tracking-[0.12em] font-bold px-2 py-0.5 border-2 border-[#0A0A0A] ${STATUS_STYLE[t.payment_status] || "bg-white text-[#0A0A0A]"}`}>{t.payment_status}</span>
                </td>
                <td className="px-4 py-3">{t.welcome_email_sent ? <span className="inline-flex items-center gap-1 text-[#1A5DFF] font-bold"><MailCheck className="w-4 h-4" /> Sent</span> : <span className="text-[#2C2C2C]">—</span>}</td>
              </tr>
            ))}
            {!busy && txns.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-[#2C2C2C]">No payments yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
